import type { EntityOptionMetadata, EntityOptionObjectCompressed } from '@einhaus/project314-types';
import { useEffect, useState } from 'react';
import { trpcPostUnbatchedClient } from '@/api/trpc.ts';
import { showToast } from '@/components/Layout/Toast.tsx';

// Helper hook to batch fetching options requests
export const useFetchOptions = (optionObjects: EntityOptionObjectCompressed[]) => {
    const [options, setOptions] = useState<EntityOptionMetadata[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let active = true;
        setOptions([]);
        setIsLoading(false);
        const fetchOptionBatch = async (batch: EntityOptionObjectCompressed[]) => {
            // Keep large option batches in the request body to avoid server URL limits.
            return trpcPostUnbatchedClient.entity.fetchOptions.query({ options: batch });
        };

        const fetchOptionsInBatches = async () => {
            const batchSize = 150;
            const batchPromises = [];

            setIsLoading(true);
            setOptions([]); // Reset options when fetching new batch

            // Create all batch promises
            for (let i = 0; i < optionObjects.length; i += batchSize) {
                const batch = optionObjects.slice(i, i + batchSize);
                batchPromises.push(fetchOptionBatch(batch));
            }

            try {
                // Execute all batch requests concurrently
                const results = await Promise.all(batchPromises);
                if (!active) return;

                // Process all results
                const allOptions: EntityOptionMetadata[] = [];

                results.forEach((response) => {
                    if (!response.success) {
                        showToast(response.errorMessages[0], 'error');
                    } else {
                        allOptions.push(...response.options);
                    }
                });

                setOptions(allOptions);
            } catch (error) {
                if (!active) return;
                showToast('Failed to fetch options', 'error');
                console.error('Error fetching options:', error);
            } finally {
                if (active) setIsLoading(false);
            }
        };

        if (optionObjects.length) void fetchOptionsInBatches();
        return () => {
            active = false;
        };
    }, [optionObjects]);

    return { isLoading, options };
};
